import React, {useEffect, useState} from 'react';
import {StyleSheet, Text, View, ScrollView, Image} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCPackageTable from '@/Atoms/CCPackageTable';
import {FontSizes, vs, WINDOW_HEIGHT} from '@/Utils/Dimensions';
import NoStanding from '@/Assets/Images/Tournaments/no_standing.svg';
import {Image as RNImage} from 'react-native';
import useGetLeaderboardApi from '@/Hooks/ClassroomInfoApi/useGetLeaderboardApi';
import Loader from '@/Atoms/CCLoader/CCLoader';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {TouchableOpacity} from 'react-native-gesture-handler';
import Box from '@/Atoms/Box';

interface LeaderboardEntry {
  user_id: string;
  name: string;
  score: number;
  solved_latest_puzzle: number;
  total_time: number;
  live_count: number;
}

interface LeaderboardResponse {
  valid: boolean;
  leaderboard: LeaderboardEntry[];
  user: LeaderboardEntry | null;
}

interface LeaderboardProps {
  meetingRole: string;
  meetingId: string;
  occurenceId: string;
  source: string;
  currentActivePuzzle: any;
  activeTabRight: number;
}

const Leaderboard: React.FC<LeaderboardProps> = ({
  meetingRole,
  meetingId,
  occurenceId,
  source,
  currentActivePuzzle,
  activeTabRight
}) => {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>(
    [],
  );
  const [currentUser, setCurrentUser] = useState<LeaderboardEntry | null>(null);
  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshLeaderboard, setRefreshLeaderboard] = useState<boolean>(false);

  const handleOnSuccessGetLeaderboard = (data: LeaderboardResponse) => {
    console.log('Fetched Leaderboard Data: ', data);

    if (data.valid && data.leaderboard) {
      setLeaderboardData(data.leaderboard);
      setCurrentUser(data.user);

      // Find and highlight current user's position in leaderboard
      if (data.user) {
        const userIndex = data.leaderboard.findIndex(
          entry => entry.user_id === data.user?.user_id,
        );
        setHighlightedRowIndex(userIndex !== -1 ? userIndex : null);
      }
    }
    setLoading(false);
  };

  const handleOnErrorGetLeaderboard = (error: any) => {
    console.error('Error fetching leaderboard:', error);
    setLoading(false);
  };

  const {mutate: getLeaderboard, isLoading} = useGetLeaderboardApi(
    handleOnSuccessGetLeaderboard,
    handleOnErrorGetLeaderboard,
  );

  useEffect(() => {
    setLoading(true);
    if (source == 'recording') {
      getLeaderboard({ meetingId, occurenceId, puzzleId: currentActivePuzzle.puzzleId || 0 });
    } else {
      getLeaderboard({ meetingId, occurenceId });
    }
    setRefreshLeaderboard(false);
  }, [meetingId, occurenceId, refreshLeaderboard, activeTabRight]);

  const headers = ['Rank', 'Player', 'Score', source === 'recording' ?  'Class Score':'Active', 'Time'];

  const getRankDisplay = (index: number) => {
    const rankIcons = {
      0: require('@/Assets/Badges/1stPrize.png'),
      1: require('@/Assets/Badges/2ndPrize.png'),
      2: require('@/Assets/Badges/3rdPrize.png'),
    };

    if (index < 3 && rankIcons[index as keyof typeof rankIcons]) {
      return (
        <Box style={{padding: vs(12), width: '100%', height: '100%'}}>
          <Box
            type="row-flex-start"
            style={{margin: 'auto', width: '100%', flexWrap: 'wrap'}}>
            <Image
              source={rankIcons[index as keyof typeof rankIcons]}
              style={styles.rankIcon}
              resizeMode="contain"
            />
          </Box>
        </Box>
      );
    }
    return (
      <Box style={{padding: vs(12), width: '100%', height: '100%'}}>
        <Box
          type="row-flex-start"
          style={{margin: 'auto', width: '100%', flexWrap: 'wrap'}}>
          <CCText fontSize={FontSizes[13]} style={styles.rankText}>
            {index + 1}
          </CCText>
        </Box>
      </Box>
    );
  };

  const rowData = leaderboardData?.map((entry, index) => {
    const isCurrentUser = currentUser && entry.user_id === currentUser.user_id;

    return {
      itemValues: [
        {
          name: getRankDisplay(index),
          type: 'component',
        },
        {
          name: (
            <Box style={{padding: vs(12), width: '100%', height: '100%'}}>
              <Box
                type="row-flex-start"
                style={{margin: 'auto', width: '100%', flexWrap: 'wrap'}}>
                <CCText
                  fontSize={FontSizes[13]}
                  numberOfLines={2}
                  ellipsizeMode={'tail'}
                  style={[
                    styles.nameText,
                    isCurrentUser && styles.currentUserText,
                  ]}>
                  {entry.name}
                </CCText>
              </Box>
            </Box>
            // <Box
            //   style={styles.nameContainer}
            //   //   style={[
            //   //     styles.nameContainer,
            //   //     isCurrentUser && {backgroundColor: CCColors.BlueAqua},
            //   //   ]}
            // >
            //   <CCText
            //     fontSize={FontSizes[13]}
            //     numberOfLines={2}
            //     ellipsizeMode={'tail'}
            //     style={[
            //       styles.nameText,
            //       isCurrentUser && styles.currentUserText,
            //     ]}>
            //     {entry.name}
            //   </CCText>
            //   {/* {isCurrentUser && (
            //     <CCText fontSize={FontSizes[12]} style={styles.youText}>
            //       (You)
            //     </CCText>
            //   )} */}
            // </Box>
          ),
          type: 'component',
        },
        {
          name: (
            <Box style={{padding: vs(12), width: '100%', height: '100%'}}>
              <Box
                type="row-flex-start"
                style={{margin: 'auto', width: '100%', flexWrap: 'wrap'}}>
                <CCText
                  fontSize={FontSizes[13]}
                  style={[
                    styles.scoreText,
                    isCurrentUser && styles.currentUserText,
                  ]}>
                  {entry.score}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'component',
        },
        {
          name: (
            <Box style={{padding: vs(12), width: '100%', height: '100%'}}>
              <Box
                type="row-flex-start"
                style={{margin: 'auto', width: '100%', flexWrap: 'wrap'}}>
                <CCText
                  fontSize={FontSizes[13]}
                  style={[
                    entry.solved_latest_puzzle === 1
                      ? styles.solvedText
                      : styles.notSolvedText,
                    isCurrentUser && styles.currentUserText,
                  ]}>
                  {source == 'recording' ? entry.live_count: entry.solved_latest_puzzle }
                  {/* {entry.solved_latest_puzzle === 1 ? '✓ Solved' : '✗ Not Solved'} */}
                </CCText>
              </Box>
            </Box>
            // <View style={styles.puzzleStatusContainer}>
            //   <CCText
            //     fontSize={FontSizes[13]}
            //     style={[
            //       entry.solved_latest_puzzle === 1
            //         ? styles.solvedText
            //         : styles.notSolvedText,
            //       isCurrentUser && styles.currentUserText,
            //     ]}>
            //     {entry.solved_latest_puzzle}
            //     {/* {entry.solved_latest_puzzle === 1 ? '✓ Solved' : '✗ Not Solved'} */}
            //   </CCText>
            // </View>
          ),
          type: 'component',
        },
        {
          name: (
            <Box style={{padding: vs(12), width: '100%', height: '100%'}}>
              <Box
                type="row-flex-start"
                style={{margin: 'auto', width: '100%', flexWrap: 'wrap'}}>
                <CCText
                  fontSize={FontSizes[13]}
                  style={[
                    styles.timeText,
                    isCurrentUser && styles.currentUserText,
                  ]}>
                  {entry.total_time}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'component',
        },
      ],
      index,
      userId: entry.user_id,
      isCurrentUser,
    };
  });

  const tableData = rowData?.map(row => row.itemValues);

  const highlightRowStyle = {
    backgroundColor: CCColors.Black,
    borderRadius: 8,
  };

  if (loading || isLoading) {
    return (
      <View style={styles.container}>
        <Loader loading={loading} bgWhite={true} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
        <CCText fontSize={FontSizes[16]} style={styles.title}>
          Leaderboard
        </CCText>
        <TouchableOpacity onPress={() => setRefreshLeaderboard(true)}>
          <Icon name="reload" color={CCColors.Primary.Brown} size={22} />
        </TouchableOpacity>
      </View>
      {currentUser && (
        <View style={styles.currentUserCard}>
          <CCText fontSize={FontSizes[14]} style={styles.currentUserCardTitle}>
            Your Performance
          </CCText>
          <View style={styles.currentUserStats}>
            <CCText fontSize={FontSizes[12]}>
              Score:{' '}
              <CCText style={styles.statValue}>{currentUser.score}</CCText>
            </CCText>
            <CCText fontSize={FontSizes[12]}>
              Time:{' '}
              <CCText style={styles.statValue}>
                {currentUser.total_time}s
              </CCText>
            </CCText>
            <CCText fontSize={FontSizes[12]}>
              Latest:{' '}
              <CCText style={styles.statValue}>
                {currentUser.solved_latest_puzzle === 1
                  ? 'Scored'
                  : 'Not Scored'}
              </CCText>
            </CCText>
          </View>
        </View>
      )}

      <ScrollView style={styles.scrollView}>
        {tableData?.length > 0 ? (
          <CCPackageTable
            headers={headers}
            tableData={tableData}
            flexArr={[0.8, 1.5, 0.8, 0.9, 1]}
            higlightedRow={
              highlightedRowIndex != null ? [highlightedRowIndex] : undefined
            }
            backgroundColorRow={true}
            tableBorder={true}
            higlightedRowStyle={highlightRowStyle}
            jsxTable={false}
            margin={1}
            borderBottomColor={CCColors?.BorderBottom}
          />
        ) : (
          <View style={styles.emptyState}>
            <RNImage
              resizeMode="contain"
              style={styles.noLeaderboardImage}
              source={NoStanding}
            />
            <CCText fontSize={FontSizes[18]} style={styles.emptyTitle}>
              No leaderboard available
            </CCText>
            <CCText fontSize={FontSizes[14]} style={styles.emptyDescription}>
              Leaderboard will appear after students solve puzzles.
            </CCText>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    height: '60%',
    minHeight: 600,
    backgroundColor: CCColors.White,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: vs(16),
    textAlign: 'center',
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  currentUserCard: {
    backgroundColor: CCColors.Primary.lightBrown,
    borderWidth: 0.3,
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  currentUserCardTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    color: CCColors?.Primary?.Dark || '#1976D2',
  },
  currentUserStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statValue: {
    fontWeight: 'bold',
    color: CCColors?.Primary?.Dark || '#1976D2',
  },
  scrollView: {
    maxHeight: WINDOW_HEIGHT / 1.2,
  },
  rankIcon: {
    width: 24,
    height: 24,
  },
  rankText: {
    fontWeight: 'bold',
    textAlign: 'center',
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  nameContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  nameText: {
    fontWeight: '500',
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  youText: {
    color: CCColors?.Primary?.Dark || '#1976D2',
    fontStyle: 'italic',
    marginTop: 2,
  },
  currentUserText: {
    fontWeight: 'bold',
    color: CCColors?.Primary?.Dark || '#1976D2',
  },
  scoreText: {
    textAlign: 'center',
    fontWeight: '500',
    // alignItems: 'center',
    // justifyContent: 'center',
  },
  puzzleStatusContainer: {
    alignItems: 'center',
  },
  solvedText: {
    color: CCColors.Green,
    fontWeight: '500',
  },
  notSolvedText: {
    color: CCColors.Red,
    fontWeight: '500',
  },
  timeText: {
    textAlign: 'center',
    fontWeight: '500',
    // alignItems: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 40,
    paddingHorizontal: 20,
  },
  noLeaderboardImage: {
    width: 120,
    height: 120,
    marginBottom: 16,
    // opacity: 0.6,
  },
  emptyTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  emptyDescription: {
    textAlign: 'center',
    color: CCColors?.TextColor?.Secondary || '#666666',
    lineHeight: 20,
  },
});

export default Leaderboard;
